import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { breakpoint_small } from '../../constants/breakpoints';
import { color_shades_dark, color_shades_darkest, color_text_light, color_text_link } from '../../constants/colors';
import SessionAppointmentReviewDataModel from '../../models/analytics/api/SessionAppointmentReviewDataModel';
import TooltipWrapper from '../general/TooltipWrapper';

const copyIcon = require('../../content/images/copy-text.svg');

interface SessionAppointmentReviewSummaryProps {
    data?: SessionAppointmentReviewDataModel | null;
    className?: string;
    inline?: boolean;
}

interface SummaryItem {
    label: string;
    value: string;
}

const SessionAppointmentReviewSummary = ({ data, className, inline }: SessionAppointmentReviewSummaryProps) => {
    const items = buildSummaryItems(data);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    useEffect(() => {
        if (!copiedKey) {
            return;
        }

        const timeout = window.setTimeout(() => {
            setCopiedKey(null);
        }, 1500);

        return () => window.clearTimeout(timeout);
    }, [copiedKey]);

    if (!items.length) {
        return null;
    }

    const copyValue = async (value: string, key: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedKey(key);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = value;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopiedKey(key);
        }
    };

    return (
        <SummaryContainer className={className} inline={inline}>
            {items.map((item) => (
                <SummaryRow key={`${item.label}-${item.value}`} title={`${item.label}: ${item.value}`}>
                    <SummaryLabel>{item.label}:</SummaryLabel>
                    <SummaryValue>{item.value}</SummaryValue>
                    <TooltipWrapper text={copiedKey === `${item.label}-${item.value}` ? 'Copied!' : `Copy ${item.label}`} place="top">
                        <CopyButton
                            type="button"
                            title={`Copy ${item.label}`}
                            aria-label={`Copy ${item.label}`}
                            onClick={() => copyValue(item.value, `${item.label}-${item.value}`)}
                            copied={copiedKey === `${item.label}-${item.value}`}
                        >
                            <CopyIcon src={copyIcon} alt="" />
                        </CopyButton>
                    </TooltipWrapper>
                </SummaryRow>
            ))}
        </SummaryContainer>
    );
};

const buildSummaryItems = (data?: SessionAppointmentReviewDataModel | null): SummaryItem[] => {
    if (!data) {
        return [];
    }

    const items: SummaryItem[] = [];

    if (data.patientId?.length) {
        items.push({
            label: 'Patient Id',
            value: data.patientId
        });
    }

    const appointmentGroups = [
        {
            getLabel: (count: number) => count === 1 ? 'Booked Appt. Id' : 'Booked Appt. Ids',
            values: data.bookedAppointmentIds
        },
        {
            getLabel: (count: number) => count === 1 ? 'Rescheduled Appt. Id' : 'Rescheduled Appt. Ids',
            values: data.rescheduledAppointmentIds
        },
        {
            getLabel: (count: number) => count === 1 ? 'Cancelled Appt. Id' : 'Cancelled Appt. Ids',
            values: data.cancelledAppointmentIds
        },
        {
            getLabel: (count: number) => count === 1 ? 'Check Next Appt. Id' : 'Check Next Appt. Ids',
            values: data.checkNextAppointmentIds
        }
    ];

    appointmentGroups.forEach((group) => {
        const values = (group.values || []).filter(value => value?.length);
        if (values.length) {
            items.push({
                label: group.getLabel(values.length),
                value: values.join(', ')
            });
        }
    });

    return items;
};

const SummaryContainer = styled.div<{ inline?: boolean }>`
    display: flex;
    flex-direction: ${props => props.inline ? 'row' : 'column'};
    flex-wrap: wrap;
    align-items: flex-start;
    gap: ${props => props.inline ? '0 12px' : '2px'};
    margin: 6px 0 0 8px;

    ${breakpoint_small} {
        margin-left: 0;
        gap: ${props => props.inline ? '2px 8px' : '2px'};
    }
`;

const SummaryRow = styled.div`
    display: flex;
    align-items: flex-start;
    max-width: 100%;
    color: ${color_shades_dark};
    font-size: 12px;
    line-height: 1.4;
`;

const SummaryLabel = styled.span`
    color: ${color_text_light};
    font-weight: 600;
    margin-right: 4px;
`;

const SummaryValue = styled.span`
    color: ${color_shades_darkest};
    word-break: break-word;
`;

const CopyButton = styled.button<{ copied?: boolean }>`
    border: none;
    background: transparent;
    color: ${color_text_link};
    cursor: pointer;
    margin-left: 6px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    opacity: ${props => props.copied ? 1 : 0.75};

    &:hover {
        opacity: 1;
    }
`;

const CopyIcon = styled.img`
    width: 13px;
    height: 13px;
    display: block;
`;

export default SessionAppointmentReviewSummary;
